import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useClients } from './ClientsContext';
import { useProducts } from './ProductsContext';
import { useCategories } from './CategoriesContext';
import { useDocuments } from './DocumentsContext';
import localApiAdapter from '../services/localApiAdapter';
import { API_BASE } from '../../shared/utils/apiConfig';
import { isCloudDemoRuntime } from '../../shared/utils/publicDemoRuntime';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import i18n from '../../i18n';

const ia = (key, opts) => i18n.t(`serviceMessages.inventoryAlerts.${key}`, opts);
const ad = (key, opts) => i18n.t(`serviceMessages.appData.${key}`, opts);

const AppDataContext = createContext();

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error(ad('hook_outside_provider'));
  }
  return context;
};

export const AppDataProvider = ({ children }) => {
  // Get real data from contexts
  const { clients, suppliers } = useClients();
  const { products } = useProducts();
  const { categories } = useCategories();
  const { createDocument, refreshDocuments } = useDocuments();
  
  // Mock employees data for the system
  const [employees] = useState(() => [
    {
      id: 1,
      mat: 'EMP001',
      nom: 'Bouaziz',
      prenom: 'Ahmed',
      dateNaissance: '1985-03-15',
      lieuNaissance: 'الرياض',
      sexe: 'M',
      situationFamiliale: ad('demo_married'),
      enfants: 2,
      adresse: '12 شارع الملك فهد، الرياض',
      ville: 'الرياض',
      codePostal: '11564',
      securiteSociale: '185037012345678',
      numCcp: '0012345678',
      cle: '90',
      fonction: ad('demo_engineer'),
      posteOccupe: ad('demo_senior_developer'),
      categorieProfessionnelle: ad('demo_executive'),
      dateRecrutement: '2020-01-15',
      dateSortie: '',
      affectation: ad('demo_it_department'),
      salaireBase: 80000,
      salaireNetMensuel: 70000,
      indemniteExperience: 8000,
      indemniteTransport: 2000,
      indemnitePanier: 2500,
      primeResponsabilite: 16000,
      primeRendementIndividuel: 12000,
      primeRendementCollectif: 8000,
      soldeCongesAnnuel: 28,
      soldeCongesMaladie: 15,
      congesPris: [
        { type: ad('demo_annual_leave'), debut: '2024-07-15', fin: '2024-07-29', jours: 14 },
        { type: ad('demo_sick_leave'), debut: '2024-09-05', fin: '2024-09-07', jours: 3 }
      ],
      documents: [
        { nom: ad('demo_work_contract'), date: '2020-01-15', status: ad('demo_signed') },
        { nom: ad('demo_job_description'), date: '2020-01-20', status: ad('demo_verified') },
        { nom: ad('demo_medical_certificate'), date: '2024-01-10', status: ad('demo_pending') }
      ],
      telephone: '0551234567',
      email: 'ahmed.bouaziz@company.com',
      typeContrat: 'CDI',
      periodeEssai: '3 mois',
      finEssai: '2020-04-15',
      permisConduire: ad('demo_yes'),
      numPermis: 'ALG123456789',
      passeport: 'ALG987654321'
    },
    {
      id: 2,
      mat: 'EMP002',
      nom: 'Amara',
      prenom: 'Fatima',
      dateNaissance: '1990-08-22',
      lieuNaissance: 'جدة',
      sexe: 'F',
      situationFamiliale: ad('demo_single'),
      enfants: 0,
      adresse: '45 طريق الملك عبدالعزيز، جدة',
      ville: 'جدة',
      codePostal: '21442',
      securiteSociale: '190082287654321',
      numCcp: '0087654321',
      cle: '15',
      fonction: ad('demo_accountant'),
      posteOccupe: ad('demo_accountant'),
      categorieProfessionnelle: ad('demo_supervisor'),
      dateRecrutement: '2021-06-01',
      dateSortie: '',
      affectation: ad('demo_accounting_department'),
      salaireBase: 60000,
      salaireNetMensuel: 52000,
      indemniteExperience: 3000,
      indemniteTransport: 2000,
      indemnitePanier: 2500,
      primeResponsabilite: 0,
      primeRendementIndividuel: 6000,
      primeRendementCollectif: 4000,
      soldeCongesAnnuel: 25,
      soldeCongesMaladie: 15,
      congesPris: [
        { type: ad('demo_annual_leave'), debut: '2024-06-10', fin: '2024-06-17', jours: 7 }
      ],
      documents: [
        { nom: ad('demo_work_contract'), date: '2021-06-01', status: ad('demo_signed') },
        { nom: ad('demo_job_description'), date: '2021-06-05', status: ad('demo_verified') }
      ],
      telephone: '0557654321',
      email: 'fatima.amara@company.com',
      typeContrat: 'CDI',
      periodeEssai: '3 mois',
      finEssai: '2021-09-01',
      permisConduire: ad('demo_no'),
      numPermis: '',
      passeport: 'ALG456789123'
    }
  ]);

  // Settings for remuneration calculations
  const [settings] = useState({
    remuneration: {
      primesRendement: {
        individuelPourcentageMax: 20,
        collectifPourcentageMax: 15
      },
      indemnites: {
        primePanier: 700,
        primeTransport: 500,
        zoneIsolee: 500
      }
    }
  });

  // Treasury data structure for CAISSE V2 components
  const [caisseData, setCaisseData] = useState(() => ({
    journalOperations: [],
    principaleOperations: [],
    mensuelleOperations: [],
    bons: [],
    transferts: [],
    soldes: {
      journal: 0,
      principale: 0,
      mensuelle: 0
    },
    vehicleRegistrations: [],
    operationTypes: [
      { id: 'RECETTE', name: ad('demo_receipt'), type: 'credit' },
      { id: 'DEPENSE', name: ad('demo_expense'), type: 'debit' },
      { id: 'TRANSFERT', name: ad('demo_transfer'), type: 'transfer' }
    ]
  }));

  // Banking data structure for BANQUE components
  const [bankingData, setBankingData] = useState(() => ({
    accounts: [],
    statements: [],
    accountingEntries: [],
    reconciliations: [],
    discrepancies: [],
    reports: [],
    reconciliationTypes: [
      { id: 'GENERAL', name: ad('demo_reconciliation_general') },
      { id: 'AUTO', name: ad('demo_reconciliation_auto') },
      { id: 'MANUAL', name: ad('demo_reconciliation_manual') }
    ]
  }));

  // Treasury methods
  const getEmployees = () => employees;

  const getSoldes = () => caisseData.soldes;

  const addJournalOperation = (operation) => {
    console.log('Adding journal operation:', operation);
    // In a real app, this would update the state
    return operation;
  };

  const transferJournalToPrincipale = (amount, description) => {
    console.log('Transfer from journal to principale:', { amount, description });
    return { success: true };
  };

  const addPrincipaleOperation = (operation) => {
    console.log('Adding principale operation:', operation);
    return operation;
  };

  const transferPrincipaleToMensuelle = (amount, description) => {
    console.log('Transfer from principale to mensuelle:', { amount, description });
    return { success: true };
  };

  const addVehicleRegistration = (vehicle) => {
    console.log('Adding vehicle registration:', vehicle);
    return vehicle;
  };

  const deleteVehicleRegistration = (vehicleId) => {
    console.log('Deleting vehicle registration:', vehicleId);
    return { success: true };
  };

  const getVehicleRegistrations = () => caisseData.vehicleRegistrations;

  const linkBonCommandeToOperation = (bonId, operationId) => {
    console.log('Linking bon commande to operation:', { bonId, operationId });
    return { success: true };
  };

  const getBonCommandeById = (bonId) => {
    console.log('Getting bon commande by ID:', bonId);
    return caisseData.bons.find(bon => bon.id === bonId) || null;
  };

  const addBonCommande = (bon) => {
    console.log('Adding bon commande:', bon);
    return bon;
  };

  const mapBankAccountForUi = (account) => {
    if (!account || typeof account !== 'object') return account;
    const bankName = account.bankName || account.bank_name || account.bank || '';
    const name = account.description || account.account_name || account.name || '';
    const statusRaw = account.status || (account.active ? 'Active' : 'Inactive');
    const status = String(statusRaw).toLowerCase() === 'active' ? 'Active' : statusRaw;
    const balance = parseFloat(account.balance ?? account.bankBalance ?? 0) || 0;
    const notesRaw = account.notes || '';
    const codeMatch = String(notesRaw).match(/^CODE=([^|]+)/);
    const accountCode = account.accountCode || account.account_code || (codeMatch ? codeMatch[1].trim() : '');
    return {
      ...account,
      description: name,
      name,
      bankName,
      bank: bankName,
      accountNumber: account.accountNumber || account.account_number || '',
      accountCode,
      status: account.reconciliation_status || (status === 'active' ? 'Active' : status),
      reconciliationStatus: account.reconciliation_status || 'pending',
      active: account.active === true || String(statusRaw).toLowerCase() === 'active',
      bankBalance: account.bankBalance ?? balance,
      accountingBalance: account.accountingBalance ?? account.accounting_balance ?? balance,
      reconciled: account.reconciled ?? 0,
      pending: account.pending ?? 0,
      discrepancies: account.discrepancies ?? 0,
    };
  };

  // Banking methods - Real SQLite API calls (demo uses in-browser storage)
  const fetchBankAccounts = useCallback(async () => {
    try {
      if (isCloudDemoRuntime()) {
        const result = await localApiAdapter.get('/banking/accounts');
        const raw = Array.isArray(result) ? result : result?.data || [];
        const accounts = raw.map(mapBankAccountForUi);
        setBankingData((prev) => ({ ...prev, accounts }));
        const { syncBanqueAccountsForTreasury } = await import('../../pages/banque/bankingTreasurySync');
        syncBanqueAccountsForTreasury(raw);
        return accounts;
      }
      const response = await fetch(`${API_BASE}/banking/accounts`);
      if (!response.ok) throw new Error(ad('fetch_bank_accounts_failed'));
      const result = await response.json();
      const raw = result.data || result;
      const accounts = raw.map(mapBankAccountForUi);
      setBankingData(prev => ({ ...prev, accounts }));
      const { syncBanqueAccountsForTreasury } = await import('../../pages/banque/bankingTreasurySync');
      syncBanqueAccountsForTreasury(raw);
      return accounts;
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (API_BASE || isCloudDemoRuntime()) {
      fetchBankAccounts();
    }
  }, [fetchBankAccounts]);

  const loadCaisseDemoData = useCallback(async () => {
    if (!isCloudDemoRuntime()) return;
    try {
      const [journal, principale, monthly, bons] = await Promise.all([
        localApiAdapter.get('/treasury/journal'),
        localApiAdapter.get('/treasury/main'),
        localApiAdapter.get('/treasury/monthly'),
        localApiAdapter.get('/treasury/bons'),
      ]);
      const asList = (r) => (Array.isArray(r) ? r : r?.data || []);
      const journalOps = asList(journal);
      const principaleOps = asList(principale);
      const mensuelleOps = asList(monthly);
      const bonsList = asList(bons);
      const sumAmount = (rows) =>
        rows.reduce((acc, row) => acc + (parseFloat(row.amount ?? row.montant) || 0), 0);
      setCaisseData((prev) => ({
        ...prev,
        journalOperations: journalOps,
        principaleOperations: principaleOps,
        mensuelleOperations: mensuelleOps,
        bons: bonsList,
        soldes: {
          journal: sumAmount(journalOps),
          principale: sumAmount(principaleOps),
          mensuelle: sumAmount(mensuelleOps),
        },
      }));
    } catch (error) {
      console.error('[demo] Failed to load caisse demo data:', error);
    }
  }, []);

  useEffect(() => {
    loadCaisseDemoData();
  }, [loadCaisseDemoData]);

  const getBankAccounts = () => bankingData.accounts;

  const encodeBankAccountNotes = (accountCode, regionOrNotes) => {
    const code = String(accountCode || '').trim();
    const extra = String(regionOrNotes || '').replace(/^CODE=[^|]+\|?/, '').trim();
    if (!code) return extra || '';
    return extra ? `CODE=${code}|${extra}` : `CODE=${code}`;
  };

  const addBankAccount = async (account) => {
    try {
      const regionNote = account.region || account.wilaya ? `Region: ${account.region || account.wilaya}` : '';
      const payload = {
        account_number: account.accountNumber || account.account_number || '',
        account_name: account.name || account.account_name || '',
        bank_name: account.bank || account.bankName || account.bank_name || '',
        account_type: 'checking',
        currency: 'SAR',
        balance: parseFloat(account.balance) || 0,
        opening_balance: parseFloat(account.balance) || 0,
        opening_date: new Date().toISOString().split('T')[0],
        status: account.active ? 'active' : 'suspended',
        iban: account.iban || '',
        swift_code: account.swiftCode || account.swift_code || '',
        branch: account.address || account.branch || '',
        notes: encodeBankAccountNotes(account.accountCode, regionNote)
      };

      const response = await fetch(`${API_BASE}/banking/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || ad('add_bank_account_failed'));
      }

      const result = await response.json();
      await fetchBankAccounts();
      return result.data || result;
    } catch (error) {
      console.error('Error adding bank account:', error);
      throw error;
    }
  };

  const updateBankAccount = async (accountId, updates) => {
    try {
      const payload = {
        account_number: updates.accountNumber || updates.account_number,
        account_name: updates.name || updates.account_name,
        bank_name: updates.bank || updates.bankName || updates.bank_name,
        account_type: updates.accountType || updates.account_type || 'checking',
        currency: updates.currency || 'SAR',
        balance: parseFloat(updates.balance) || 0,
        opening_balance: parseFloat(updates.opening_balance) || 0,
        opening_date: updates.opening_date || null,
        status: updates.active !== undefined 
          ? (updates.active ? 'active' : 'suspended')
          : (updates.status === 'Inactive' || updates.status === 'inactive' ? 'suspended' : (updates.status || 'active')),
        iban: updates.iban || '',
        swift_code: updates.swiftCode || updates.swift_code || '',
        branch: updates.address || updates.branch || '',
        notes: encodeBankAccountNotes(
          updates.accountCode,
          updates.notes || (updates.region || updates.wilaya ? `Region: ${updates.region || updates.wilaya}` : '')
        )
      };

      const response = await fetch(`${API_BASE}/banking/accounts/${accountId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || ad('update_bank_account_failed'));
      }

      await fetchBankAccounts();
      return { success: true };
    } catch (error) {
      console.error('Error updating bank account:', error);
      throw error;
    }
  };

  const deleteBankAccount = async (accountId) => {
    try {
      const response = await fetch(`${API_BASE}/banking/accounts/${accountId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || ad('delete_bank_account_failed'));
      }

      await fetchBankAccounts();
      return { success: true };
    } catch (error) {
      console.error('Error deleting bank account:', error);
      throw error;
    }
  };

  const getBankStatements = () => bankingData.statements;

  const addBankStatement = (statement) => {
    console.log('Adding bank statement:', statement);
    return statement;
  };

  const getAccountingEntries = () => bankingData.accountingEntries;

  const addAccountingEntry = (entry) => {
    console.log('Adding accounting entry:', entry);
    return entry;
  };

  const getReconciliations = () => bankingData.reconciliations;

  const addReconciliation = (reconciliation) => {
    console.log('Adding reconciliation:', reconciliation);
    return reconciliation;
  };

  const performAutoReconciliation = (accountId) => {
    console.log('Performing auto reconciliation for account:', accountId);
    return { success: true, matchedTransactions: [] };
  };

  const getDiscrepancies = () => bankingData.discrepancies;

  const resolveDiscrepancy = (discrepancyId, resolution) => {
    console.log('Resolving discrepancy:', { discrepancyId, resolution });
    return { success: true };
  };

  // Purchases and Sales data - Load from localStorage for persistence
  const [purchasesData, setPurchasesData] = useState(() => {
    try {
      const saved = localStorage.getItem('qawaem-purchases');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading purchases data:', error);
      return [];
    }
  });
  
  const [salesData, setSalesData] = useState(() => {
    try {
      const saved = localStorage.getItem('qawaem-sales');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading sales data:', error);
      return [];
    }
  });
  const [ordersData] = useState([]);
  
  // Generate mock sales data for analytics
  const generateMockSalesData = () => {
    const productList = getProducts();
    const customerList = getCustomers();
    
    if (productList.length === 0 || customerList.length === 0) return [];
    
    const mockSales = [];
    const currentDate = new Date();
    
    // Generate sales for the past 12 months
    for (let monthsAgo = 0; monthsAgo < 12; monthsAgo++) {
      const salesCount = Math.floor(Math.random() * 10) + 5; // 5-15 sales per month
      
      for (let i = 0; i < salesCount; i++) {
        const saleDate = new Date(currentDate);
        saleDate.setMonth(currentDate.getMonth() - monthsAgo);
        saleDate.setDate(Math.floor(Math.random() * 28) + 1); // Random day in month
        
        // Random customer
        const customer = customerList[Math.floor(Math.random() * customerList.length)];
        
        // Random 1-4 products per sale
        const itemCount = Math.floor(Math.random() * 4) + 1;
        const saleItems = [];
        let saleSubtotal = 0;
        
        for (let j = 0; j < itemCount; j++) {
          const product = productList[Math.floor(Math.random() * productList.length)];
          const quantity = Math.floor(Math.random() * 5) + 1;
          const price = parseFloat(product.price) || 100;
          const total = quantity * price;
          
          saleItems.push({
            id: product.id,
            productId: product.id,
            name: product.name,
            quantity: quantity,
            price: price,
            unitPrice: price,
            total: total,
            unit: product.unit || 'pcs'
          });
          
          saleSubtotal += total;
        }
        
        const tax = saleSubtotal * 0.19; // 19% VAT
        const saleTotal = saleSubtotal + tax;
        
        mockSales.push({
          id: `sale-${monthsAgo}-${i}`,
          invoiceNumber: `INV-${saleDate.getFullYear()}-${String(saleDate.getMonth() + 1).padStart(2, '0')}-${String(i + 1).padStart(3, '0')}`,
          date: saleDate.toISOString().split('T')[0],
          rawDate: saleDate.toISOString(),
          customerId: customer.id,
          customerName: customer.name,
          items: saleItems,
          subtotal: saleSubtotal,
          tax: tax,
          discount: 0,
          total: saleTotal,
          status: 'completed',
          paymentStatus: Math.random() > 0.1 ? 'paid' : 'pending',
          paymentMethod: ['cash', 'card', 'bank_transfer'][Math.floor(Math.random() * 3)],
          notes: ad('auto_sale_note')
        });
      }
    }
    
    return mockSales.sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));
  };
  
  // Stock and inventory data
  const [stockItemsData] = useState([]);
  const [stockValuationData] = useState({});

  const getPurchases = () => purchasesData;
  
  const addPurchase = async (purchaseData) => {
    try {
      console.log('Adding purchase:', purchaseData);
      const purchaseId = `purchase-${Date.now()}`;
      
      // Create purchase entry
      const newPurchase = {
        id: purchaseId,
        purchaseNumber: purchaseData.purchaseNumber || `ACH-${Date.now()}`,
        supplierId: purchaseData.supplierId,
        supplier: purchaseData.supplier || ad('unknown_supplier'),
        amount: purchaseData.amount || 0,
        date: purchaseData.date || new Date().toISOString(),
        status: purchaseData.status || ad('status_in_progress'),
        items: purchaseData.items || [],
        notes: purchaseData.notes || '',
        paymentMethod: purchaseData.paymentMethod || ad('unspecified'),
        documentId: purchaseData.documentId || null,
        documentType: purchaseData.documentType || null,
        source: purchaseData.source || 'MANUAL',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add to state and localStorage
      const updatedPurchases = [...purchasesData, newPurchase];
      setPurchasesData(updatedPurchases);
      localStorage.setItem('qawaem-purchases', JSON.stringify(updatedPurchases));
      
      // Auto-create corresponding document if purchase is completed
      if (purchaseData.status === 'completed' && createDocument && purchaseData.source === 'MANUAL') {
        const supplier = suppliers?.find(s => s.id === purchaseData.supplierId) || {};
        
        const documentData = {
          type: 'purchase-order',
          number: purchaseData.purchaseNumber || `PO-${Date.now()}`,
          date: purchaseData.date || new Date().toISOString().split('T')[0],
          clientId: purchaseData.supplierId, // Supplier as "client" for purchase documents
          clientName: supplier.name || supplier.company || ad('supplier'),
          clientEmail: supplier.email || '',
          clientPhone: supplier.phone || '',
          clientAddress: supplier.address || '',
          items: purchaseData.items || [],
          subtotal: purchaseData.subtotal || 0,
          tax: purchaseData.tax || 0,
          discount: purchaseData.discount || 0,
          total: purchaseData.total || 0,
          status: 'completed',
          notes: purchaseData.notes || '',
          reference: purchaseData.reference || '',
          paymentMethod: purchaseData.paymentMethod || 'bank_transfer',
          paymentStatus: purchaseData.paymentStatus || 'pending'
        };
        
        const documentResult = await createDocument(documentData);
        console.log('Auto-created purchase document:', documentResult);
        
        // Refresh documents to show the new document
        if (refreshDocuments) {
          await refreshDocuments();
        }
      }
      
      return { success: true, id: purchaseId, purchase: newPurchase };
    } catch (error) {
      console.error('Error adding purchase:', error);
      return { success: false, error: error.message };
    }
  };

  const updatePurchase = (purchaseId, updateData) => {
    console.log('Updating purchase:', { purchaseId, updateData });
    return { success: true };
  };

  const deletePurchase = (purchaseId) => {
    console.log('Deleting purchase:', purchaseId);
    return { success: true };
  };

  const getSales = () => {
    // Return existing sales data if available, otherwise generate mock data for analytics
    if (salesData.length > 0) {
      return salesData;
    }
    
    // Generate mock sales data for analytics if we have products and customers
    const mockSales = generateMockSalesData();
    return mockSales;
  };
  
  const addSale = async (saleData) => {
    try {
      console.log('Adding sale:', saleData);
      const saleId = `sale-${Date.now()}`;
      
      // Create sale entry
      const newSale = {
        id: saleId,
        saleNumber: saleData.saleNumber || `VTE-${Date.now()}`,
        customerId: saleData.customerId,
        customer: saleData.customer || 'عميل غير معروف',
        amount: saleData.amount || 0,
        date: saleData.date || new Date().toISOString(),
        status: saleData.status || ad('status_in_progress'),
        items: saleData.items || [],
        notes: saleData.notes || '',
        paymentMethod: saleData.paymentMethod || ad('unspecified'),
        documentId: saleData.documentId || null,
        documentType: saleData.documentType || null,
        source: saleData.source || 'MANUAL',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add to state and localStorage
      const updatedSales = [...salesData, newSale];
      setSalesData(updatedSales);
      localStorage.setItem('qawaem-sales', JSON.stringify(updatedSales));
      
      // Auto-create corresponding document if sale is completed
      if (saleData.status === 'completed' && createDocument && saleData.source === 'MANUAL') {
        const customer = clients?.find(c => c.id === saleData.customerId) || {};
        
        const documentData = {
          type: 'invoice',
          number: saleData.invoiceNumber || `INV-${Date.now()}`,
          date: saleData.date || new Date().toISOString().split('T')[0],
          clientId: saleData.customerId,
          clientName: customer.name || customer.company || ad('client'),
          clientEmail: customer.email || '',
          clientPhone: customer.phone || '',
          clientAddress: customer.address || '',
          items: saleData.items || [],
          subtotal: saleData.subtotal || 0,
          tax: saleData.tax || 0,
          discount: saleData.discount || 0,
          total: saleData.total || 0,
          status: 'completed',
          notes: saleData.notes || '',
          reference: saleData.reference || '',
          paymentMethod: saleData.paymentMethod || 'cash',
          paymentStatus: saleData.paymentStatus || 'paid',
          dueDate: saleData.dueDate || ''
        };
        
        const documentResult = await createDocument(documentData);
        console.log('Auto-created sales document:', documentResult);
        
        // Refresh documents to show the new document
        if (refreshDocuments) {
          await refreshDocuments();
        }
      }
      
      return { success: true, id: saleId, sale: newSale };
    } catch (error) {
      console.error('Error adding sale:', error);
      return { success: false, error: error.message };
    }
  };

  const updateSale = (saleId, updateData) => {
    console.log('Updating sale:', { saleId, updateData });
    return { success: true };
  };

  const deleteSale = (saleId) => {
    console.log('Deleting sale:', saleId);
    return { success: true };
  };

  const getSuppliers = () => {
    // Return real suppliers data from ClientsContext
    return suppliers ? suppliers.map(supplier => ({
      id: supplier.id,
      name: supplier.name || supplier.company || ad('name_undefined'),
      email: supplier.email || '',
      phone: supplier.phone || supplier.phone2 || '',
      address: supplier.address || '',
      city: supplier.city || '',
      taxNumber: supplier.taxNumber || supplier.vatNumber || supplier.nif || '',
      type: supplier.type || 'company',
      category: supplier.category || '',
      status: supplier.status || 'active'
    })) : [];
  };

  const getCustomers = () => {
    // Return real clients data from ClientsContext  
    return clients ? clients.map(client => ({
      id: client.id,
      name: client.name || client.company || ad('name_undefined'),
      email: client.email || '',
      phone: client.phone || client.phone2 || '',
      address: client.address || '',
      city: client.city || '',
      taxNumber: client.taxNumber || client.vatNumber || client.nif || '',
      type: client.type || 'company',
      category: client.category || '',
      status: client.status || 'active'
    })) : [];
  };


  // Additional methods needed by components
  const getSaleById = (saleId) => {
    const sales = getSales();
    return sales.find(sale => sale.id === saleId) || null;
  };

  const getPurchaseById = (purchaseId) => {
    const purchases = getPurchases();
    return purchases.find(purchase => purchase.id === purchaseId) || null;
  };

  const getProducts = () => {
    // Return real products data from ProductsContext
    return products ? products.map(product => ({
      id: product.id,
      name: product.name || ad('product_unnamed'),
      price: parseFloat(product.price) || 0,
      cost: parseFloat(product.cost) || 0,
      category: product.category || '',
      categoryId: product.categoryId || '',
      barcode: product.barcode || '',
      reference: product.reference || '',
      description: product.description || '',
      unit: product.unit || 'pcs',
      stock: parseInt(product.stock) || 0,
      minStock: parseInt(product.minStock) || 0,
      maxStock: parseInt(product.maxStock) || 0,
      type: product.type || 'product',
      status: product.status || 'active',
      supplier: product.supplier || '',
      location: product.location || '',
      tax: parseFloat(product.tax) || 0
    })) : [];
  };

  const getWarehouses = () => {
    // Mock warehouses data
    return [
      { id: 'wh-1', name: ad('demo_warehouse_main'), location: ad('demo_industrial_zone'), capacity: 1000 },
      { id: 'wh-2', name: ad('demo_warehouse_secondary'), location: ad('demo_city_center'), capacity: 500 }
    ];
  };

  const getCategories = () => {
    // Return real categories data from CategoriesContext
    return categories ? categories.map(category => ({
      id: category.id,
      name: category.name || ad('category_unnamed'),
      description: category.description || '',
      type: category.type || 'product',
      color: category.color || '#3B82F6',
      icon: category.icon || 'Package',
      status: category.status || 'active'
    })) : [];
  };

  const getDocumentTypes = () => {
    // Mock document types for forms
    return [
      { id: 'invoice', name: ad('demo_doc_invoice'), code: 'FAC' },
      { id: 'quote', name: ad('demo_doc_quote'), code: 'DEV' },
      { id: 'delivery', name: ad('demo_doc_delivery'), code: 'BL' },
      { id: 'purchase-order', name: ad('demo_doc_purchase_order'), code: 'BC' }
    ];
  };

  const getOrders = () => {
    // Mock orders data
    return ordersData || [
      {
        id: 'ord-1',
        customerId: 'client-1',
        orderDate: new Date().toISOString(),
        status: 'pending',
        items: [
          { productId: 'prod-1', quantity: 2, unitPrice: 100, total: 200 }
        ],
        subtotal: 200,
        tax: 38,
        discount: 0,
        total: 238,
        notes: ad('test_order_note')
      }
    ];
  };

  // Convert order to sale (order-to-sale pipeline)
  // Auto-populate invoice from purchase order
  const createInvoiceFromPurchase = async (purchase) => {
    try {
      console.log('Creating invoice from purchase:', purchase);
      
      if (!purchase) {
        throw new Error(ad('purchase_data_required'));
      }
      
      // Get supplier details
      const supplier = getSuppliers().find(s => s.id === purchase.supplierId);
      
      // Create invoice data from purchase order
      const invoiceData = {
        type: 'invoice',
        number: `INV-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        dueDate: purchase.dueDate || null,
        supplierId: purchase.supplierId,
        contactName: supplier?.name || purchase.supplierName || ad('supplier'),
        contactEmail: supplier?.email || '',
        contactPhone: supplier?.phone || '',
        contactAddress: supplier?.address || '',
        items: purchase.items || [],
        subtotal: purchase.subtotal || 0,
        taxRate: purchase.tax || 19,
        taxAmount: (purchase.subtotal || 0) * ((purchase.tax || 19) / 100),
        total: purchase.total || 0,
        notes: ad('invoice_from_purchase_note', { number: purchase.purchaseNumber || purchase.id }),
        paymentMethod: purchase.paymentMethod || null,
        status: 'draft',
        originalPurchaseId: purchase.id
      };
      
      console.log('Invoice data created from purchase:', invoiceData);
      return {
        success: true,
        invoiceData: invoiceData,
        message: ad('invoice_data_generated')
      };
    } catch (error) {
      console.error('Error creating invoice from purchase:', error);
      return {
        success: false,
        message: error.message || ad('invoice_generation_error')
      };
    }
  };

  // Document to Transaction Integration
  const createTransactionFromDocument = async (documentData) => {
    try {
      const documentType = documentData.type || documentData.docType;
      
      // Define document type mappings - ONLY documents that represent ACTUAL completed transactions
      const salesDocuments = ['invoice', 'delivery-note']; // Only actual sales, not quotes/proforma
      const purchaseDocuments = ['purchase-order', 'reception-note', 'return-slip']; // Only actual purchases, not quote requests
      
      if (salesDocuments.includes(documentType)) {
        // Create sale entry
        const saleData = {
          id: `sale-doc-${documentData.id || Date.now()}`,
          saleNumber: documentData.number || documentData.documentNumber,
          documentId: documentData.id,
          documentType: documentType,
          customerId: documentData.clientId,
          customer: documentData.clientName || documentData.client?.name || 'عميل غير معروف',
          amount: documentData.amount || documentData.total || 0,
          date: documentData.date || new Date().toISOString(),
          status: documentData.status || ad('status_in_progress'),
          items: documentData.items || [],
          notes: ad('auto_generated_from_document', { number: documentData.number }),
          paymentMethod: documentData.paymentMethod || ad('unspecified'),
          source: 'DOCUMENT',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        await addSale(saleData);
        console.log('Sale created from document:', saleData);
        
      } else if (purchaseDocuments.includes(documentType)) {
        // Determine if this is a return (positive) or regular purchase (negative)
        const isReturn = documentType === 'return-slip';
        const baseAmount = documentData.amount || documentData.total || 0;
        
        // Create purchase entry
        const purchaseData = {
          id: `purchase-doc-${documentData.id || Date.now()}`,
          purchaseNumber: documentData.number || documentData.documentNumber,
          documentId: documentData.id,
          documentType: documentType,
          supplierId: documentData.supplierId || documentData.clientId,
          supplier: documentData.supplierName || documentData.clientName || documentData.client?.name || ad('unknown_supplier'),
          amount: isReturn ? Math.abs(baseAmount) : baseAmount, // Returns are positive (money back)
          date: documentData.date || new Date().toISOString(),
          status: documentData.status || ad('status_in_progress'),
          items: documentData.items || [],
          notes: isReturn
            ? ad('return_refund_from_document', { number: documentData.number })
            : ad('auto_generated_from_document', { number: documentData.number }),
          paymentMethod: documentData.paymentMethod || ad('unspecified'),
          source: 'DOCUMENT',
          transactionType: isReturn ? 'RETURN' : 'PURCHASE', // Mark return type
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        await addPurchase(purchaseData);
        console.log(isReturn ? 'Return (positive) created from document:' : 'Purchase created from document:', purchaseData);
      }
      
      // Update stock quantities based on document type
      await updateStockFromDocument(documentData);
      
      return { success: true, message: ad('transaction_created') };
      
    } catch (error) {
      console.error('Error creating transaction from document:', error);
      return { success: false, error: error.message };
    }
  };

  // Update stock quantities based on document type
  const updateStockFromDocument = async (documentData) => {
    try {
      const documentType = documentData.type || documentData.docType;
      
      // Documents that increase stock (incoming)
      const stockIncreaseDocuments = ['reception-note', 'return-slip'];
      // Documents that decrease stock (outgoing)  
      const stockDecreaseDocuments = ['delivery-note', 'shipping-slip'];
      
      if (documentData.items && Array.isArray(documentData.items)) {
        documentData.items.forEach(item => {
          const product = products.find(p => p.id === item.productId || p.name === item.name);
          
          if (product) {
            let adjustment = 0;
            
            if (stockIncreaseDocuments.includes(documentType)) {
              adjustment = item.quantity || 0; // Increase stock
            } else if (stockDecreaseDocuments.includes(documentType)) {
              adjustment = -(item.quantity || 0); // Decrease stock
            }
            
            if (adjustment !== 0) {
              // Log stock movement
              console.log(`Stock adjustment for ${product.name}: ${adjustment > 0 ? '+' : ''}${adjustment}`);
            }
          }
        });
      }
      
    } catch (error) {
      console.error('Error updating stock from document:', error);
    }
  };

  // Bidirectional sync - Update documents when Achats/Ventes change
  const syncDocumentFromTransaction = async (transactionData, transactionType) => {
    try {
      if (transactionData.documentId && updateDocument) {
        // Find and update the corresponding document
        const documentUpdates = {
          status: transactionData.status,
          amount: transactionData.amount,
          date: transactionData.date,
          notes: transactionData.notes,
          updatedAt: new Date().toISOString(),
          syncedFromTransaction: true
        };

        await updateDocument(transactionData.documentId, documentUpdates);
        console.log(`✅ Document ${transactionData.documentId} synced from ${transactionType}`);
      }
      
      return { success: true };
    } catch (error) {
      console.error(`Error syncing document from ${transactionType}:`, error);
      return { success: false, error: error.message };
    }
  };

  // Update linked document when transaction status changes
  const updateTransactionStatus = async (transactionId, transactionType, newStatus, additionalData = {}) => {
    try {
      const isUpdatingSale = transactionType === 'SALE';
      const currentData = isUpdatingSale ? salesData : purchasesData;
      const setData = isUpdatingSale ? setSalesData : setPurchasesData;
      const storageKey = isUpdatingSale ? 'qawaem-sales' : 'qawaem-purchases';

      // Find and update the transaction
      const updatedData = currentData.map(transaction => {
        if (transaction.id === transactionId) {
          const updatedTransaction = {
            ...transaction,
            status: newStatus,
            ...additionalData,
            updatedAt: new Date().toISOString()
          };

          // Sync with linked document if exists
          if (transaction.documentId) {
            syncDocumentFromTransaction(updatedTransaction, transactionType);
          }

          return updatedTransaction;
        }
        return transaction;
      });

      // Update state and localStorage
      setData(updatedData);
      localStorage.setItem(storageKey, JSON.stringify(updatedData));

      return { success: true };
    } catch (error) {
      console.error(`Error updating ${transactionType} status:`, error);
      return { success: false, error: error.message };
    }
  };

  const convertOrderToSale = async (order) => {
    try {
      console.log('Converting order to sale:', order);
      
      if (!order) {
        throw new Error(ad('order_data_required'));
      }
      
      // Create sale data from order
      const saleData = {
        customerId: order.customerId,
        customerName: order.customerName,
        items: order.items || [],
        subtotal: order.subtotal || 0,
        tax: order.tax || 0,
        discount: order.discount || 0,
        total: order.total || 0,
        date: new Date().toISOString().split('T')[0],
        status: 'completed',
        paymentStatus: 'pending',
        paymentMethod: 'to_determine',
        notes: ad('convert_from_order_note', { number: order.orderNumber || order.id }),
        originalOrderId: order.id
      };
      
      // Add the sale using existing addSale function
      const result = await addSale(saleData);
      
      if (result.success) {
        console.log('Order successfully converted to sale:', result);
        return {
          success: true,
          saleId: result.saleId,
          message: ad('order_converted_to_sale')
        };
      } else {
        throw new Error(result.message || ad('create_sale_failed'));
      }
    } catch (error) {
      console.error('Error converting order to sale:', error);
      return {
        success: false,
        message: error.message || ad('convert_order_error')
      };
    }
  };

  // Stock and inventory functions
  const getStockItems = () => {
    // Generate stock movements based on real purchases and sales data
    const stockMovements = [];
    const purchases = getPurchases();
    const sales = getSales();
    
    // Add movements from purchases
    purchases.forEach(purchase => {
      if (purchase.items && Array.isArray(purchase.items)) {
        purchase.items.forEach(item => {
          stockMovements.push({
            id: `stock-purchase-${purchase.id}-${item.id}`,
            productId: item.id || item.productId,
            type: 'purchase',
            quantity: parseInt(item.quantity) || 0,
            unitPrice: parseFloat(item.price || item.unitPrice) || 0,
            date: purchase.date || new Date().toISOString(),
            reference: purchase.purchaseNumber || `PO-${purchase.id}`,
            notes: ad('purchase_note', { notes: purchase.notes || ad('stock_receipt_default') }),
            documentId: purchase.id,
            supplierId: purchase.supplierId,
            warehouseId: purchase.warehouseId || 'wh-1'
          });
        });
      }
    });
    
    // Add movements from sales  
    sales.forEach(sale => {
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          stockMovements.push({
            id: `stock-sale-${sale.id}-${item.id}`,
            productId: item.id || item.productId,
            type: 'sale',
            quantity: -(parseInt(item.quantity) || 0), // Negative for outgoing stock
            unitPrice: parseFloat(item.price || item.unitPrice) || 0,
            date: sale.date || new Date().toISOString(),
            reference: sale.invoiceNumber || `INV-${sale.id}`,
            notes: ad('sale_note', { notes: sale.notes || ad('stock_exit_default') }),
            documentId: sale.id,
            customerId: sale.customerId,
            warehouseId: sale.warehouseId || 'wh-1'
          });
        });
      }
    });
    
    // Sort movements by date (newest first)
    return stockMovements.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getStockValuation = () => {
    return stockValuationData;
  };

  const refreshInventoryData = async () => {
    console.log('Refreshing inventory data...');
    // In a real implementation, this would refresh data from API
    return { success: true };
  };

  const fetchAllData = async () => {
    console.log('Fetching all data...');
    // In a real implementation, this would refresh all data from API
    return { success: true };
  };

  // Inventory adjustment and transfer functions with document creation
  const createInventoryAdjustment = async (adjustmentData) => {
    try {
      console.log('Creating inventory adjustment:', adjustmentData);
      
      // Create corresponding document for audit trail
      if (createDocument) {
        const documentData = {
          type: 'stock-sheet',
          number: adjustmentData.reference || `ADJ-${Date.now()}`,
          date: adjustmentData.date || new Date().toISOString().split('T')[0],
          clientId: '', // No client for internal adjustments
          clientName: ad('stock_adjustment'),
          clientEmail: '',
          clientPhone: '',
          clientAddress: '',
          items: adjustmentData.items || [],
          subtotal: 0,
          tax: 0,
          discount: 0,
          total: 0,
          status: 'completed',
          notes: adjustmentData.notes || ad('auto_stock_adjustment'),
          reference: adjustmentData.reference || '',
          warehouseId: adjustmentData.warehouseId || 'wh-1',
          adjustmentType: adjustmentData.adjustmentType || 'correction',
          reason: adjustmentData.reason || ''
        };
        
        const documentResult = await createDocument(documentData);
        console.log('Auto-created adjustment document:', documentResult);
        
        if (refreshDocuments) {
          await refreshDocuments();
        }
      }
      
      return { success: true, id: `adjustment-${Date.now()}` };
    } catch (error) {
      console.error('Error creating inventory adjustment:', error);
      return { success: false, error: error.message };
    }
  };

  const createInventoryTransfer = async (transferData) => {
    try {
      console.log('Creating inventory transfer:', transferData);
      
      // Create corresponding document for audit trail
      if (createDocument) {
        const documentData = {
          type: 'material-exit',
          number: transferData.reference || `TRF-${Date.now()}`,
          date: transferData.date || new Date().toISOString().split('T')[0],
          clientId: '', // No client for internal transfers
          clientName: ad('stock_transfer', {
            from: transferData.fromWarehouse || ad('warehouse'),
            to: transferData.toWarehouse || ad('warehouse'),
          }),
          clientEmail: '',
          clientPhone: '',
          clientAddress: '',
          items: transferData.items || [],
          subtotal: 0,
          tax: 0,
          discount: 0,
          total: 0,
          status: transferData.status || 'pending',
          notes: transferData.notes || ad('auto_stock_transfer'),
          reference: transferData.reference || '',
          fromWarehouseId: transferData.fromWarehouseId || 'wh-1',
          toWarehouseId: transferData.toWarehouseId || 'wh-2',
          transferType: 'internal'
        };
        
        const documentResult = await createDocument(documentData);
        console.log('Auto-created transfer document:', documentResult);
        
        if (refreshDocuments) {
          await refreshDocuments();
        }
      }
      
      return { success: true, id: `transfer-${Date.now()}` };
    } catch (error) {
      console.error('Error creating inventory transfer:', error);
      return { success: false, error: error.message };
    }
  };

  // Get inventory alerts for dashboard notifications
  const getInventoryAlerts = () => {
    const productList = getProducts();
    
    // Categorize products by stock status (same logic as ThresholdTab)
    const outOfStockProducts = productList.filter(p => (p.stock || 0) <= 0);
    const criticalProducts = productList.filter(p => (p.stock || 0) > 0 && (p.stock || 0) <= (p.minStock || 0) / 2);
    const lowStockProducts = productList.filter(p => (p.stock || 0) > (p.minStock || 0) / 2 && (p.stock || 0) <= (p.minStock || 0));
    
    // Generate alerts array for dashboard notifications
    const alerts = [];
    
    // Critical alerts (out of stock)
    outOfStockProducts.forEach(product => {
      alerts.push({
        id: `out-of-stock-${product.id}`,
        type: 'error',
        severity: 'critical',
        title: ia('out_of_stock_title'),
        message: ia('out_of_stock_message', { product: product.name }),
        productId: product.id,
        productName: product.name,
        currentStock: product.stock || 0,
        minStock: product.minStock || 0,
        category: 'inventory',
        timestamp: new Date().toISOString(),
        action: 'reorder'
      });
    });
    
    // High priority alerts (critical stock)
    criticalProducts.forEach(product => {
      alerts.push({
        id: `critical-stock-${product.id}`,
        type: 'warning',
        severity: 'high',
        title: ia('critical_stock_title'),
        message: ia('critical_stock_message', { product: product.name, count: product.stock || 0 }),
        productId: product.id,
        productName: product.name,
        currentStock: product.stock || 0,
        minStock: product.minStock || 0,
        category: 'inventory',
        timestamp: new Date().toISOString(),
        action: 'reorder'
      });
    });
    
    // Medium priority alerts (low stock)
    lowStockProducts.forEach(product => {
      alerts.push({
        id: `low-stock-${product.id}`,
        type: 'info',
        severity: 'medium',
        title: ia('low_stock_title'),
        message: ia('low_stock_message', {
          product: product.name,
          current: product.stock || 0,
          min: product.minStock || 0,
        }),
        productId: product.id,
        productName: product.name,
        currentStock: product.stock || 0,
        minStock: product.minStock || 0,
        category: 'inventory',
        timestamp: new Date().toISOString(),
        action: 'monitor'
      });
    });
    
    return {
      alerts: alerts.sort((a, b) => {
        const severityOrder = { 'critical': 3, 'high': 2, 'medium': 1, 'low': 0 };
        return severityOrder[b.severity] - severityOrder[a.severity];
      }),
      summary: {
        outOfStock: outOfStockProducts.length,
        critical: criticalProducts.length,
        low: lowStockProducts.length,
        total: outOfStockProducts.length + criticalProducts.length + lowStockProducts.length
      }
    };
  };

  // Financial functions for Recettes & Dépenses integration
  const getIncome = async () => {
    try {
      console.log('📊 Getting detailed income data for profit report...');
      
      // Get data from both documents and sales transactions
      const allDocuments = await localApiAdapter.documents.getDocuments();
      const salesData = getSales();
      
      // Filter income from documents (invoices, etc.)
      const incomeDocuments = allDocuments.filter(doc => 
        doc.type === 'invoice' || doc.type === 'facture' || doc.type === 'income' || doc.type === 'recette'
      );
      
      const documentIncome = incomeDocuments.map(doc => ({
        id: doc.id,
        date: doc.date || new Date().toISOString().split('T')[0],
        category: doc.type === 'invoice' || doc.type === 'facture' ? 'ventes' : 
                 doc.category || 'services',
        label: doc.title || doc.description || ad('invoice_label', { number: doc.number || doc.id }),
        amount: parseFloat(doc.total || doc.amount || 0),
        paymentMethod: doc.payment_method || 'virement',
        reference: doc.number || doc.reference || doc.id,
        client: doc.client_name || doc.client || ad('unspecified_client'),
        type: 'document',
        items: doc.items || []
      }));
      
      // Add detailed sales transactions with product breakdown
      const salesIncome = salesData.map(sale => ({
        id: `sale-${sale.id}`,
        date: sale.date || new Date().toISOString().split('T')[0],
        category: 'ventes',
        label: ad('sale_label', { number: sale.saleNumber || sale.id }),
        amount: parseFloat(sale.amount || 0),
        paymentMethod: sale.paymentMethod || 'especes',
        reference: sale.saleNumber || sale.id,
        client: sale.customer || ad('unspecified_client'),
        type: 'sales-transaction',
        items: sale.items || [],
        productDetails: sale.items ? sale.items.map(item => ({
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          revenue: item.subtotal || (item.quantity * item.unitPrice)
        })) : []
      }));
      
      const allIncome = [...documentIncome, ...salesIncome];
      const totalIncome = allIncome.reduce((sum, income) => sum + income.amount, 0);
      
      console.log(`📈 Found ${allIncome.length} total income entries:`);
      console.log(`   - ${documentIncome.length} from documents`);
      console.log(`   - ${salesIncome.length} from sales transactions`);
      console.log(`   - Total income: ${totalIncome} DA`);
      
      return allIncome;
    } catch (error) {
      console.error('❌ Error loading income data:', error);
      return [];
    }
  };

  const getExpenses = async () => {
    try {
      console.log('📊 Getting detailed expenses data for profit report...');
      
      // Get data from both documents and purchase transactions
      const allDocuments = await localApiAdapter.documents.getDocuments();
      const purchasesData = getPurchases();
      
      // Filter expenses from documents
      const expenseDocuments = allDocuments.filter(doc => 
        doc.type === 'expense' || doc.type === 'depense' || doc.type === 'purchase' || 
        doc.type === 'achat' || doc.type === 'bon_commande'
      );
      
      const documentExpenses = expenseDocuments.map(doc => ({
        id: doc.id,
        date: doc.date || new Date().toISOString().split('T')[0],
        category: doc.type === 'purchase' || doc.type === 'achat' ? 'achats' : 
                 doc.category || 'general',
        label: doc.title || doc.description || ad('expense_label', { number: doc.number || doc.id }),
        amount: parseFloat(doc.total || doc.amount || 0),
        paymentMethod: doc.payment_method || 'especes',
        reference: doc.number || doc.reference || doc.id,
        supplier: doc.supplier_name || doc.supplier || ad('unspecified_supplier'),
        type: 'document',
        items: doc.items || []
      }));
      
      // Add detailed purchase transactions with product breakdown
      const purchaseExpenses = purchasesData.map(purchase => ({
        id: `purchase-${purchase.id}`,
        date: purchase.date || new Date().toISOString().split('T')[0],
        category: 'achats',
        label: ad('purchase_label', { number: purchase.purchaseNumber || purchase.id }),
        amount: parseFloat(purchase.amount || 0),
        paymentMethod: purchase.paymentMethod || 'virement',
        reference: purchase.purchaseNumber || purchase.id,
        supplier: purchase.supplier || ad('unspecified_supplier'),
        type: 'purchase-transaction',
        items: purchase.items || [],
        productDetails: purchase.items ? purchase.items.map(item => ({
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          cost: item.subtotal || (item.quantity * item.unitPrice)
        })) : []
      }));
      
      const allExpenses = [...documentExpenses, ...purchaseExpenses];
      const totalExpenses = allExpenses.reduce((sum, expense) => sum + expense.amount, 0);
      
      console.log(`📉 Found ${allExpenses.length} total expense entries:`);
      console.log(`   - ${documentExpenses.length} from documents`);
      console.log(`   - ${purchaseExpenses.length} from purchase transactions`);
      console.log(`   - Total expenses: ${totalExpenses} DA`);
      
      return allExpenses;
    } catch (error) {
      console.error('❌ Error loading expenses data:', error);
      return [];
    }
  };

  // Enhanced product-level profit analysis
  const getProductProfitAnalysis = async () => {
    try {
      console.log('📈 Calculating detailed product profit analysis...');
      
      const incomeData = await getIncome();
      const expensesData = await getExpenses();
      const productsData = getProducts();
      
      const productAnalysis = new Map();
      
      // Process sales income by product
      incomeData.forEach(income => {
        if (income.productDetails && income.productDetails.length > 0) {
          income.productDetails.forEach(product => {
            const key = product.productId || product.productName;
            if (!productAnalysis.has(key)) {
              productAnalysis.set(key, {
                productId: product.productId,
                productName: product.productName,
                totalRevenue: 0,
                totalCost: 0,
                totalQuantitySold: 0,
                totalQuantityPurchased: 0,
                averageSalePrice: 0,
                averageCostPrice: 0,
                profit: 0,
                marginPercent: 0
              });
            }
            
            const analysis = productAnalysis.get(key);
            analysis.totalRevenue += product.revenue || 0;
            analysis.totalQuantitySold += product.quantity || 0;
          });
        }
      });
      
      // Process purchase expenses by product
      expensesData.forEach(expense => {
        if (expense.productDetails && expense.productDetails.length > 0) {
          expense.productDetails.forEach(product => {
            const key = product.productId || product.productName;
            if (!productAnalysis.has(key)) {
              productAnalysis.set(key, {
                productId: product.productId,
                productName: product.productName,
                totalRevenue: 0,
                totalCost: 0,
                totalQuantitySold: 0,
                totalQuantityPurchased: 0,
                averageSalePrice: 0,
                averageCostPrice: 0,
                profit: 0,
                marginPercent: 0
              });
            }
            
            const analysis = productAnalysis.get(key);
            analysis.totalCost += product.cost || 0;
            analysis.totalQuantityPurchased += product.quantity || 0;
          });
        }
      });
      
      // Calculate final metrics
      const productProfits = Array.from(productAnalysis.values()).map(analysis => {
        analysis.averageSalePrice = analysis.totalQuantitySold > 0 
          ? analysis.totalRevenue / analysis.totalQuantitySold 
          : 0;
        analysis.averageCostPrice = analysis.totalQuantityPurchased > 0 
          ? analysis.totalCost / analysis.totalQuantityPurchased 
          : 0;
        analysis.profit = analysis.totalRevenue - analysis.totalCost;
        analysis.marginPercent = analysis.totalRevenue > 0 
          ? ((analysis.profit / analysis.totalRevenue) * 100)
          : 0;
        
        return analysis;
      }).sort((a, b) => b.profit - a.profit); // Sort by profit descending
      
      console.log(`📊 Analyzed ${productProfits.length} products for profitability`);
      return productProfits;
    } catch (error) {
      console.error('❌ Error calculating product profit analysis:', error);
      return [];
    }
  };

  const value = {
    employees,
    getEmployees,
    settings,
    // Direct data access for analytics components
    products: getProducts(),
    sales: getSales(),
    customers: getCustomers(),
    suppliers: getSuppliers(),
    categories: getCategories(),
    warehouses: getWarehouses(),
    purchases: getPurchases(),
    stockAdjustments: getStockItems().filter(item => item.type === 'ajustement'),
    // Treasury data and methods
    caisseData,
    getSoldes,
    addJournalOperation,
    transferJournalToPrincipale,
    addPrincipaleOperation,
    transferPrincipaleToMensuelle,
    addVehicleRegistration,
    deleteVehicleRegistration,
    getVehicleRegistrations,
    linkBonCommandeToOperation,
    getBonCommandeById,
    addBonCommande,
    // Banking data and methods
    bankingData,
    getBankAccounts,
    fetchBankAccounts,
    addBankAccount,
    updateBankAccount,
    deleteBankAccount,
    getBankStatements,
    addBankStatement,
    getAccountingEntries,
    addAccountingEntry,
    getReconciliations,
    addReconciliation,
    performAutoReconciliation,
    getDiscrepancies,
    resolveDiscrepancy,
    // Purchases and Sales methods
    getPurchases,
    addPurchase,
    updatePurchase,
    deletePurchase,
    getSales,
    addSale,
    updateSale,
    deleteSale,
    getSuppliers,
    getCustomers,
    getSaleById,
    getPurchaseById,
    getProducts,
    getWarehouses,
    getCategories,
    getDocumentTypes,
    getOrders,
    convertOrderToSale,
    createInvoiceFromPurchase,
    createTransactionFromDocument,
    updateStockFromDocument,
    syncDocumentFromTransaction,
    updateTransactionStatus,
    // Stock and inventory functions
    stockItems: getStockItems(),
    getStockItems,
    stockValuation: getStockValuation(),
    getStockValuation,
    refreshInventoryData,
    fetchAllData,
    // Inventory document creation functions
    createInventoryAdjustment,
    createInventoryTransfer,
    // Inventory alerts for dashboard
    getInventoryAlerts,
    // Financial data functions for profit reports
    getIncome,
    getExpenses,
    getProductProfitAnalysis
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};


